import { initKeyboardShortcuts } from './keyboardShortcuts.js';
import { initContextMenu } from './contextMenu.js';
import { clearSnapHighlights, initNodeInteractions } from './nodeInteractions.js';
import type { NodeInteractionViewState } from './nodeInteractions.js';
import { initViewportInteractions } from './viewportInteractions.js';
import { initCutGesture } from './cutGesture.js';
import { createCutConnections } from './cutConnections.js';
import { getDotWorldPosFor } from './portGeometry.js';
import type { NodeEditorController } from './nodeEditor.js';
import type { AppState } from '../state/appState.js';
import type { HistoryStore } from '../editor/historyState.js';
import type { CommandBatch } from '../editor/commandHistory.js';
import type { EditorCommands } from '../editor/commands.js';
import type { EditorConnectionMutations } from '../editor/connectionMutations.js';
import type { ConnectionStateController } from './connectionState.js';
import type { EdgeVisualStateController } from './edgeVisualState.js';

interface GraphInteractionDeps {
  appState: AppState;
  historyStore: Pick<HistoryStore, 'performUndo' | 'performRedo'>;
  commandBatch: CommandBatch;
  commands: Pick<EditorCommands, 'moveNode'>;
  connectionMutations: Pick<EditorConnectionMutations, 'cutEdgesWithHistory'>;
  connectionState: ConnectionStateController;
  edgeVisualState: EdgeVisualStateController;
  nodeInteractionViewState: NodeInteractionViewState;
  nodeEditor: NodeEditorController;
  renderEditorNavigation: () => void;
  renderSceneTree: () => void;
  updateParams: () => void;
  scheduleRender: () => void;
  isNodeEditorShortcutActive: () => boolean;
}

export function initGraphInteractions(deps: GraphInteractionDeps): void {
  const graphArea = document.getElementById('graph-canvas-area')!;
  const getDotWorldPos = (nodeId: string, portIdx: number, isOut: boolean) => (
    getDotWorldPosFor(deps.appState.nodes, nodeId, portIdx, isOut)
  );
  const cutConnections = createCutConnections({
    getEdges: () => deps.appState.edges,
    getDotWorldPos,
    cutEdgesWithHistory: deps.connectionMutations.cutEdgesWithHistory,
    renderGraph: deps.nodeEditor.renderGraph,
    scheduleRender: deps.scheduleRender,
  });
  const cutGesture = initCutGesture(graphArea, {
    clearSnapHighlights,
    getEdges: () => deps.appState.edges,
    getDotWorldPos,
    visualState: deps.edgeVisualState,
    hasPendingConnection: () => !!deps.connectionState.pendingConn,
    refreshRenderedConnections: deps.nodeEditor.renderConnections,
    severEdgesIntersectingLine: cutConnections.severEdgesIntersectingLine,
  });
  initNodeInteractions(graphArea, {
    commandBatch: deps.commandBatch,
    connectionState: deps.connectionState,
    viewState: deps.nodeInteractionViewState,
    moveNode: deps.commands.moveNode,
    nodeEditor: deps.nodeEditor,
  });
  initViewportInteractions(graphArea, {
    hasPendingConnection: () => !!deps.connectionState.pendingConn,
    nodeEditor: deps.nodeEditor,
  });

  initContextMenu(graphArea, {
    appState: deps.appState,
    nodeEditor: deps.nodeEditor,
    scheduleRender: deps.scheduleRender,
    updateParams: deps.updateParams,
  });
  initKeyboardShortcuts({
    appState: deps.appState,
    connectionState: deps.connectionState,
    historyStore: deps.historyStore,
    nodeEditor: deps.nodeEditor,
    renderEditorNavigation: deps.renderEditorNavigation,
    renderSceneTree: deps.renderSceneTree,
    updateParams: deps.updateParams,
    scheduleRender: deps.scheduleRender,
    isNodeEditorShortcutActive: deps.isNodeEditorShortcutActive,
    clearSnapHighlights,
    hasCutGesture: cutGesture.hasActive,
    clearCutGesture: cutGesture.clear,
  });
}
